export const DEMO_CAMPAIGN = {
  name: 'Demo Campaign',
  category: 'Summer HVAC',
  tagline: 'Get more AC repair calls before the season shifts to fall. One step at a time — do them in order.',
};

export const DEMO_PREREQS = [
  {
    id: 'my_brand',
    label: 'Fill in My Brand — name, phone, city, license, logo → your ads look professional and can go live.',
    internalLink: '/brand',
    internalLinkText: 'Open My Brand',
  },
  {
    id: 'fb_page',
    label: 'A Facebook Business Page for your company',
    help: 'Don\u2019t have one? It takes 10 minutes:',
    helpLink: 'https://www.facebook.com/business/help/1199464373557428',
    helpLinkText: 'Facebook\u2019s page setup guide',
  },
  {
    id: 'meta_payment',
    label: 'A payment method added to Meta Ads Manager (a business card or bank account)',
  },
  {
    id: 'gbp_access',
    label: 'Access to your Google Business Profile (you\u2019ll need it for the Google ad in Step 2)',
  },
  {
    id: 'phone',
    label: 'A phone that can take calls and texts during business hours — this whole campaign is built to make it ring',
  },
  {
    id: 'job_value',
    label: "Know your real price for this service — you'll enter it on each campaign's Performance card, so the money numbers are YOUR numbers, not ours",
  },
];

export const DEMO_STEPS = [
  {
    id: 1,
    title: 'Run a Facebook Ad',
    shortDesc: 'A picture ad your neighbors see on Facebook & Instagram',
    whatThisIs: 'A picture ad that shows up in your neighbors\u2019 Facebook and Instagram feeds.',
    whyUsing: 'Homeowners with a broken AC are scrolling Facebook right now. This ad puts your phone number in front of them so your phone rings this week.',
    channel: 'Meta Ads Manager (facebook.com/adsmanager)',
    whenToUse: 'Today.',
    whatToDo: [
      'Go to facebook.com/adsmanager and log in with your business Facebook account.',
      'Find the button labeled "Create" (it starts a new ad campaign) and pick "Leads", then "Calls".',
      'Set your daily budget to $20.',
      'Under "Audience", keep Meta Advantage+ turned ON (it\u2019s on by default) — Meta\u2019s AI will find the right homeowners for you. Just set the location to 15 miles around your shop and leave everything else alone.',
      'Copy the Ad Headline below and paste it into the "Headline" box.',
      'Copy the Ad Text below and paste it into the "Primary text" box.',
      'Upload a photo of you or your tech next to an AC unit (a phone photo is fine — real beats fancy).',
      'When it asks which phone number the ad should call, use your campaign tracking number — [Your tracking number will appear here] — so every call from this ad gets counted back to this campaign.',
      'Click "Publish". Heads up: Meta reviews every new ad before it runs. Yours may say "In review" for a few hours before it shows "Active" — that\u2019s normal, nothing is broken.',
    ],
    metrics: [
      'Cost per call: typically $25\u2013$50 per call for HVAC. If you\u2019re well above that after a week, try a different photo or headline.',
      'Don\u2019t judge it on day one — Meta Advantage+ needs time to learn who to show your ad to. Wait 1 full week before making any changes, then decide.',
    ],
    roiCalc: (jobValue) => {
      const v = Math.round(jobValue);
      const weekly = v * 2;
      return `One AC repair pays you about $${v.toLocaleString()}. If this ad brings in 5 calls a week and you book just 2 of them, that\u2019s about $${weekly.toLocaleString()} a week back from roughly $140 of ad spend.`;
    },
    nextStep: 'Once this ad is published, move to Step 2 and set up your Google ad — it catches the people who are searching instead of scrolling.',
    assets: [
      {
        label: 'Ad Headline (copy this)',
        content: 'AC Broken? We Can Be There Today.',
      },
      {
        label: 'Ad Text (copy this)',
        content: "It's the hottest week of the year — and your AC picked NOW to quit? \n\nWe get it. That's why we hold same-day slots for AC repairs all summer.\n\n✅ Same-day service\n✅ Up-front pricing — no surprises\n✅ Fixed right or you don't pay\n\nTap the call button and we'll get you cool again today.",
      },
      {
        label: 'Tracking number (placeholder)',
        content: '[Your tracking number will appear here]\n\nUse this number as the phone number in the ad — NOT your regular business line. That way, every call this ad brings in is counted for this campaign, and you\u2019ll know exactly what it\u2019s earning you.',
      },
      {
        label: 'Photo tip',
        content: 'Use a real photo: you or your tech, smiling, next to an outdoor AC unit or your truck. Skip stock photos — homeowners can tell.',
      },
    ],
  },
  {
    id: 2,
    title: 'Run a Google Search Ad',
    shortDesc: 'A text ad for people Googling "AC repair near me"',
    whatThisIs: 'A text ad that shows at the top of Google when someone near you searches "AC repair near me".',
    whyUsing: 'People searching Google for AC repair need help right now — they are the easiest jobs you will ever book. This ad catches them before your competitors do.',
    channel: 'Google Ads (ads.google.com)',
    whenToUse: 'After Step 1 is published. Start this today or tomorrow.',
    whatToDo: [
      'Go to ads.google.com and sign in with your business Google account.',
      'Click "+ New campaign" and choose "Get more calls".',
      'Set your daily budget to $25.',
      'Set the location to 15 miles around your shop.',
      'Copy the 3 Headlines below into the headline boxes.',
      'Copy the 2 Descriptions below into the description boxes.',
      'Copy the Keywords below into the keywords box — keep the quote marks, they matter.',
      'Add your phone number when it asks, then click "Publish".',
    ],
    metrics: [
      'Cost per call: under $50 = working.',
      'If you spend $150 with no calls, check that your keywords still have the quote marks around them.',
    ],
    roi: 'A Google caller is usually ready to book on the spot. If this brings 3 calls a week and you book 2 at $450 each, that\u2019s about $900 a week from about $175 of ad spend.',
    nextStep: 'While the ads warm up, go to Step 3 and put up a free post on your Facebook page — it costs nothing and backs up your ads.',
    assets: [
      {
        label: 'Headlines (copy all 3)',
        content: 'AC Repair — Same Day Service\nLocal AC Repair Near You\nUp-Front Pricing, No Surprises',
      },
      {
        label: 'Descriptions (copy both)',
        content: "AC down in this heat? We hold same-day slots all summer. Call now and we'll get you cool today.\nFamily-run, licensed & insured. You approve the price before we start any work.",
      },
      {
        label: 'Keywords (copy exactly, with quotes)',
        content: '"ac repair near me"\n"air conditioner repair"\n"ac not cooling"\n"ac repair same day"\n"emergency ac repair"',
      },
    ],
  },
  {
    id: 3,
    title: 'Post on Your Facebook Page',
    shortDesc: 'A free post for your business page — costs nothing',
    whatThisIs: 'A free post on your business Facebook page. No ad spend — just you talking to people who already follow you.',
    whyUsing: 'When your paid ads make someone curious, they check your page first. A fresh, helpful post makes you look active and trustworthy — and it books jobs by itself, for free.',
    channel: 'Your business Facebook page (post it from your phone is fine)',
    whenToUse: 'After Step 2 is running for 3 days.',
    whatToDo: [
      'Open your business Facebook page.',
      'Click "Create post".',
      'Copy the post below and paste it in.',
      'Add a real photo — you on a job, your truck, or a before/after of a repair.',
      'Post it between 7 and 9 in the morning — that\u2019s when homeowners scroll.',
      'When people comment, reply to every single one the same day.',
    ],
    metrics: [
      '3 or more comments or shares = working. Reply to each one — replies turn into bookings.',
    ],
    roi: 'This post costs you $0. If it books even one AC repair, that\u2019s about $450 of pure gain — and posts like this often get shared into local neighborhood groups where the real gold is.',
    nextStep: 'Now go to Step 4 and email your past customers — they already trust you, so they\u2019re your fastest wins.',
    assets: [
      {
        label: 'Facebook Post (copy this)',
        content: "🌡️ Real talk from your local AC guy:\n\nThe #1 call we get every August? \u201cMy AC was making a weird noise for weeks... and now it's dead.\u201d\n\nThat noise is your AC asking for help. Catch it early and it's usually a cheap fix. Wait, and it can turn into a replacement.\n\nIf your AC is doing ANY of these, give us a shout:\n🔸 Weird rattling or buzzing\n🔸 Blowing air that isn't cold\n🔸 Running non-stop without cooling the house\n\nWe hold same-day slots all summer. One call, and you're cool again. ❄️\n\n📞 [YOUR PHONE NUMBER]",
      },
    ],
  },
  {
    id: 4,
    title: 'Email Your Past Customers',
    shortDesc: 'A friendly check-in email to people you already served',
    whatThisIs: 'A short, friendly email to customers you\u2019ve already done work for.',
    whyUsing: 'These people already know and trust you. One email can fill your schedule this week — and it costs nothing. This is usually the fastest money in this whole campaign.',
    channel: 'Your email — Gmail, Outlook, or Mailchimp if you have it',
    whenToUse: 'The morning after your Facebook post goes up.',
    whatToDo: [
      'Make a list of past customer emails (from your invoices, booking system, or phone).',
      'Copy the Subject Line below into the subject box.',
      'Copy the Email below into the body.',
      'Replace [YOUR NAME] and [YOUR PHONE NUMBER] with your real info.',
      'If you\u2019re using Gmail: put YOUR address in "To" and paste customers into "BCC" so they can\u2019t see each other.',
      'Send it between 8 and 10 in the morning.',
    ],
    metrics: [
      'For every 100 emails sent, 2 to 3 replies or bookings = working.',
    ],
    roi: 'If you email 100 past customers and just 2 book a $150 tune-up that turns up a $450 repair each, that\u2019s up to $1,200 from an email that took you 10 minutes.',
    nextStep: 'Great — the core campaign is running. Step 5 sends a short text to past customers who didn\u2019t open the email.',
    assets: [
      {
        label: 'Subject Line (copy this)',
        content: 'Quick AC check before the heat breaks?',
      },
      {
        label: 'Email (copy this)',
        content: "Hi there,\n\nIt's [YOUR NAME] — we did some work at your place a while back.\n\nQuick heads up: we're in the last stretch of AC season, and this is when tired units give out. A 20-minute check now usually catches small problems while they're still cheap to fix.\n\nWe're holding tune-up slots for past customers this week and next. Want one? Just reply to this email or call/text [YOUR PHONE NUMBER].\n\nStay cool,\n[YOUR NAME]",
      },
    ],
  },
  {
    id: 5,
    title: 'Text Your Past Customers',
    shortDesc: 'A short text message follow-up',
    whatThisIs: 'A short, friendly text message to past customers who didn\u2019t answer your email.',
    whyUsing: 'Almost everyone reads texts, usually within minutes. This catches the people who missed your email and turns them into booked jobs.',
    channel: 'Your phone (regular texting is fine to start)',
    whenToUse: '2 days after the email from Step 4 goes out.',
    whatToDo: [
      'Pull up your past customer phone numbers.',
      'Copy the text below and replace [YOUR NAME] with your name.',
      'Send it person by person — do NOT send one giant group text.',
      'Send between 10 in the morning and 6 in the evening.',
      'When someone replies, answer within the hour if you can.',
    ],
    metrics: [
      'For every 50 texts, 3 to 5 replies = working.',
    ],
    roi: 'Texts get answered when emails don\u2019t. If 50 texts book 3 tune-ups and one turns into a $450 repair, that\u2019s several hundred dollars for 20 minutes of thumb work.',
    nextStep: 'Last one: Step 6 turns this week\u2019s happy customers into 5-star reviews that keep feeding you calls after the campaign ends.',
    assets: [
      {
        label: 'Text Message (copy this)',
        content: "Hi, it's [YOUR NAME] — your AC/heating guy. End-of-summer heads up: we're doing quick AC check-ups for past customers this week before fall hits. Want me to pencil you in? Just reply YES and I'll text you a time.",
      },
    ],
  },
  {
    id: 6,
    title: 'Ask This Week\u2019s Customers for a Review',
    shortDesc: 'Turn happy customers into 5-star Google reviews',
    whatThisIs: 'A simple ask you send every customer right after you finish their job.',
    whyUsing: 'Reviews are what make strangers pick YOU from the Google list. Every review you collect now keeps bringing in calls long after this campaign is over.',
    channel: 'Text message, right after the job is done',
    whenToUse: 'Same day as every completed job — while they\u2019re still happy and the house is cool.',
    whatToDo: [
      'Search Google for your own business name and copy the link to your Google profile.',
      'Copy the text below and paste your review link where it says [LINK].',
      'Send it to every customer within an hour of finishing their job.',
      'When a review comes in, reply to it with a short thank-you.',
    ],
    metrics: [
      'About half the people you ask should leave a review. If it\u2019s way less, ask in person first, then send the link.',
    ],
    roi: 'Going from 10 reviews to 30 reviews can double your calls from Google — without spending another dollar on ads. Each review is a little salesperson working for you for free, forever.',
    nextStep: 'That\u2019s the whole campaign. Keep the ads running while they\u2019re profitable, and re-run Steps 3–6 once a month. When fall arrives, switch the ads to heating tune-ups.',
    assets: [
      {
        label: 'Review Ask Text (copy this)',
        content: "So glad we could get you cooled off today! If you have 60 seconds, a quick Google review helps our small business more than you'd believe: [LINK]\n\nThanks again! 🙏",
      },
    ],
  },
];
